
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '@mui/material';
import { LinePath, Line } from '@visx/shape';
import { AxisBottom, AxisLeft } from '@visx/axis';
import { LegendOrdinal, LegendItem, LegendLabel } from '@visx/legend';
import { scaleLinear, scalePoint, scaleOrdinal } from '@visx/scale';
import { extent } from 'd3-array';
import { defaultPieChartColorRange } from '@components/pie-chart/utils';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { getChartTooltipStyles, getChartLegendBoxStyles, chartTooltipRowStyle, getChartTooltipSwatchStyle } from '@utils/chart-styles';

// Vertical tick label component for narrow widths with many labels
const VerticalTickLabel = ({ 
  x = 0, 
  y = 0, 
  text, 
  fill, 
  fontSize
}: any) => {
  const words = String(text).split(" ");
  const verticalLineHeight = 14;
  
  return (
    <g transform={`rotate(-90, ${x}, ${y})`}>
      <text 
        x={x} 
        y={y} 
        fill={fill} 
        fontSize={fontSize} 
        textAnchor="end"
        dy="0.35em"
        dx="-0.5em"
      >
        {words.map((word: string, idx: number) => (
          <tspan key={`word-${idx}`} x={x} dx="-0.5em" dy={idx === 0 ? 0 : verticalLineHeight}>
            {word}
          </tspan>
        ))}
      </text>
    </g>
  );
};

export interface MultiLineChartSeries {
  id: string;
  color?: string;
  data: { x: string | number; y: number }[];
}

export interface MultiLineChartProps {
  data: MultiLineChartSeries[];
  xLabel?: string;
  yLabel?: string;
  minHeight?: number;
  textColor?: string;
  lineColor?: string;
}

export const MultiLineChart: React.FC<MultiLineChartProps> = ({
  data,
  xLabel,
  yLabel,
  minHeight = 300,
  textColor = "#000",
  lineColor = "#ccc",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateDimensions = () => {
      const { width, height } = container.getBoundingClientRect();
      setDimensions({ 
        width, 
        height: Math.max(height, minHeight) 
      });
    };

    // Initial measurement after layout
    updateDimensions();

    // Set up ResizeObserver to handle container size changes
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [minHeight]);

  return (
    <div 
      ref={containerRef} 
      style={{ width: '100%', minHeight: `${minHeight}px`, height: '100%' }}
    >
      {dimensions.width > 0 && (
        <MultiLineChartInner
          data={data}
          xLabel={xLabel}
          yLabel={yLabel}
          width={dimensions.width}
          height={dimensions.height}
          textColor={textColor}
          lineColor={lineColor}
        />
      )}
    </div>
  );
};

interface MultiLineChartInnerProps {
  data: MultiLineChartSeries[];
  xLabel?: string;
  yLabel?: string;
  width: number;
  height: number;
  textColor: string;
  lineColor: string;
}

interface TooltipData {
  xValue: string | number;
  points: { id: string; y: number; color: string }[];
}

const MultiLineChartInner: React.FC<MultiLineChartInnerProps> = ({
  data,
  xLabel,
  yLabel,
  width,
  height,
  textColor,
  lineColor,
}) => {
  const theme = useTheme();
  const tooltipStyles = getChartTooltipStyles(theme);
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
  } = useTooltip<TooltipData>();

  // Collect all unique x values - preserve order from first series (already sorted chronologically)
  const allX = useMemo(
    () => (data.length > 0 ? data[0].data.map(d => d.x) : []),
    [data]
  );
  
  // Determine if we should use vertical labels
  const shouldUseVerticalLabels = allX.length > 6 && width < 900;
  
  // Responsive legend: underneath when narrow, right side when wide
  const isNarrow = width < 700;
  const legendWidth = isNarrow ? 0 : 130;
  const chartWidth = Math.max(width - legendWidth, 200);
  // Reserve more space for legend below when there are many series
  const legendBottomSpace = isNarrow ? (data.length > 8 ? 75 : 60) : 0;
  const chartHeight = height - legendBottomSpace;
  
  // Dynamic bottom margin for vertical labels
  const bottomMargin = useMemo(() => 
    shouldUseVerticalLabels ? 120 : 70, 
    [shouldUseVerticalLabels]
  );
  const margin = { top: 40, right: 80, bottom: bottomMargin, left: 60 };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;

  // Find y min/max
  const allY = data.flatMap(series => series.data.map(d => d.y));
  const yDomain = extent(allY) as [number, number];

  // Scales
  const xScale = scalePoint({
    domain: allX,
    range: [0, innerWidth],
    padding: 0.5,
  });
  const yScale = scaleLinear({
    domain: [Math.min(0, yDomain[0]), yDomain[1]],
    range: [innerHeight, 0],
    nice: true,
  });
  const colorScale = scaleOrdinal({
    domain: data.map(s => s.id),
    range: defaultPieChartColorRange,
  });

  // Handle mouse move - find closest x value
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<SVGRectElement>) => {
      const point = localPoint(event);
      if (!point) return;

      const mouseX = point.x - margin.left;
      
      // Find the closest x value
      let closestX: string | number | null = null;
      let minDistance = Infinity;
      
      allX.forEach(x => {
        const xPos = xScale(x) ?? 0;
        const distance = Math.abs(mouseX - xPos);
        if (distance < minDistance) {
          minDistance = distance;
          closestX = x;
        }
      });

      if (closestX !== null) {
        // Get all series values at this x
        const points = data.map(series => {
          const dataPoint = series.data.find(d => d.x === closestX);
          return {
            id: series.id,
            y: dataPoint?.y ?? 0,
            color: series.color || colorScale(series.id),
          };
        }).filter(p => p.y !== 0 || data.some(s => s.data.find(d => d.x === closestX && d.y === 0)));

        const xPos = xScale(closestX) ?? 0;
        
        showTooltip({
          tooltipData: { xValue: closestX, points },
          tooltipLeft: xPos + margin.left,
          tooltipTop: point.y,
        });
      }
    },
    [data, allX, xScale, colorScale, margin.left, showTooltip]
  );

  return (
    <div style={{ display: "flex", flexDirection: isNarrow ? "column" : "row", width: "100%", height: "100%", gap: "0", alignItems: isNarrow ? "center" : "flex-start", position: "relative" }}>
      <svg
        width={chartWidth}
        height={chartHeight}
        style={{ display: "block", flexShrink: 0 }}
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {data.map((series) => (
            <LinePath
              key={series.id}
              data={series.data}
              x={d => xScale(d.x) ?? 0}
              y={d => yScale(d.y) ?? 0}
              stroke={series.color || colorScale(series.id)}
              strokeWidth={2}
            />
          ))}
          {/* Vertical indicator line on hover */}
          {tooltipOpen && tooltipData && (
            <Line
              from={{ x: (tooltipLeft ?? 0) - margin.left, y: 0 }}
              to={{ x: (tooltipLeft ?? 0) - margin.left, y: innerHeight }}
              stroke="#999"
              strokeWidth={1}
              strokeDasharray="4,4"
              pointerEvents="none"
            />
          )}
          {/* Circles at data points on hover */}
          {tooltipOpen && tooltipData && tooltipData.points.map((point) => {
            const dataPoint = data.find(s => s.id === point.id)?.data.find(d => d.x === tooltipData.xValue);
            if (!dataPoint) return null;
            return (
              <circle
                key={point.id}
                cx={xScale(dataPoint.x) ?? 0}
                cy={yScale(dataPoint.y) ?? 0}
                r={4}
                fill={point.color}
                stroke="white"
                strokeWidth={2}
                pointerEvents="none"
              />
            );
          })}
          {/* Invisible overlay for mouse events */}
          <rect
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onMouseMove={handleMouseMove}
            onMouseLeave={hideTooltip}
          />
        </g>
        {/* Axes */}
        <AxisBottom
          top={margin.top + innerHeight}
          scale={xScale}
          label={xLabel}
          stroke={lineColor}
          tickStroke={lineColor}
          tickComponent={({ formattedValue, ...tickProps }) => 
            shouldUseVerticalLabels ? (
              <VerticalTickLabel
                {...tickProps}
                text={formattedValue}
                fill={textColor}
                fontSize={11}
              />
            ) : (
              <text
                {...tickProps}
                fill={textColor}
                fontSize={11}
                textAnchor="middle"
              >
                {formattedValue}
              </text>
            )
          }
          labelProps={{
            fill: textColor,
            fontSize: 11,
            textAnchor: 'middle',
            dy: shouldUseVerticalLabels ? '5em' : '2.5em',
          }}
          left={margin.left}
        />
        <AxisLeft
          left={margin.left}
          scale={yScale}
          label={yLabel}
          stroke={lineColor}
          tickStroke={lineColor}
          tickLabelProps={() => ({
            fill: textColor,
            fontSize: 11,
            textAnchor: 'end',
            dx: '-0.33em',
          })}
          labelProps={{
            fill: textColor,
            fontSize: 11,
            textAnchor: 'middle',
            dy: '0em',
          }}
          top={margin.top}
        />
      </svg>
      {/* Legend - underneath when narrow, right side when wide */}
      <div style={{ 
        display: "flex", 
        flexDirection: isNarrow ? "row" : "column",
        flexWrap: isNarrow ? "wrap" : "nowrap",
        justifyContent: isNarrow ? "center" : "flex-start", 
        padding: "4px",
        ...getChartLegendBoxStyles(theme),
        borderRadius: "4px",
        minWidth: isNarrow ? "auto" : "100px",
        maxWidth: isNarrow ? "100%" : "120px",
        fontSize: "10px",
        gap: isNarrow ? "8px" : "0",
      }}>
        <LegendOrdinal scale={colorScale} labelFormat={id => id} direction={isNarrow ? "row" : "column"}>
          {(labels) =>
            labels.map((label, i) => (
              <LegendItem key={i} margin={isNarrow ? "0 8px 0 0" : "0 0 4px 0"}>
                <svg width={10} height={10} style={{ marginRight: 3, flexShrink: 0 }}>
                  <rect fill={label.value} width={10} height={10} />
                </svg>
                <LegendLabel style={{ fontSize: "10px", wordWrap: "break-word", overflowWrap: "break-word" }}>{label.text}</LegendLabel>
              </LegendItem>
            ))
          }
        </LegendOrdinal>
      </div>
      {/* Tooltip */}
      {tooltipOpen && tooltipData && (
        <TooltipWithBounds
          left={tooltipLeft}
          top={tooltipTop}
          style={tooltipStyles}
        >
          <div style={{ fontWeight: 600, marginBottom: '6px', borderBottom: `1px solid ${theme.palette.divider}`, paddingBottom: '4px' }}>
            {tooltipData.xValue}
          </div>
          {tooltipData.points.map((point) => (
            <div key={point.id} style={{ ...chartTooltipRowStyle, marginTop: '4px' }}>
              <span style={getChartTooltipSwatchStyle(point.color)} />
              <span style={{ fontSize: '12px' }}>{point.id}:</span>
              <span style={{ fontSize: '12px', fontWeight: 500 }}>{point.y.toLocaleString()}</span>
            </div>
          ))}
        </TooltipWithBounds>
      )}
    </div>
  );
};
